import { API } from '@/modules/api'
import type { EPayTrakFee } from '@/types/ePayTrakFees'
import type { TEPayTrakFeeCartLine } from '@/types/cart'
import { OrderItemType, OrderItemTypeCode } from '@/types/orderItemType'
import { defineStore } from 'pinia'
import { useCartStore } from '@/stores/cart'
import { useStore } from '@/stores'

export const createUniqueId = (feeId: number, orderItemType: number) => `${feeId}::${orderItemType}`

export const useEPayTrakFeesStore = defineStore('ePayTrakFees', {
  state: () => ({
    isLoaded: {} as Record<TDistrictId, boolean>,
    fees: {} as Record<TDistrictId, EPayTrakFee[]>
  }),

  getters: {
    isLoading(state) {
      return (districtId: TDistrictId) => !(state.isLoaded[districtId] ?? false)
    },
    getFees(state) {
      return (districtId: TDistrictId) => state.fees[districtId] ?? []
    }
  },

  actions: {
    async fetch(_districtId: TDistrictId) {
      this.isLoaded[_districtId] = false
      const store = useStore()
      const fees = store.districts.find(district => district.districtId === _districtId)?.fees ?? []
      this.fees[_districtId] = fees
      this.autoAdd(_districtId)
      this.validateCart(_districtId)
      this.isLoaded[_districtId] = true
    },

    autoAdd(districtId: TDistrictId) {
      this.getFees(districtId).forEach(feeData => {
        const cartData = {
          feeId: feeData.feeId,
          orderItemType: OrderItemType.EpaytrakCartFee,
          amount: feeData.amountDue,
          description: feeData.description,
          allowPartial: feeData.allowPartial,
          feeCategory: feeData.feeCategory,
          isPartial: feeData.allowPartial
        }

        this.addToCart(districtId, cartData)
      })
    },

    // This function is used to validate the cart lines for the family user invoice store
    // It checks if the cart lines are valid compared to the data from the API, and if not, it removes them from the cart.
    // This prevents the cart from attempting to get order line estimates or checking out with invalid data.
    validateCart(districtId: TDistrictId) {
      const cartStore = useCartStore()

      for (const line of cartStore.getCartLines(districtId) as TEPayTrakFeeCartLine[]) {
        if (line.orderItemType === OrderItemType.EpaytrakCartFee) {
          const fee = this.fees[districtId].find(fee => fee.feeId === line.feeId)
          if (!fee) {
            console.log('ePayTrak fee not found, removing from cart', line.feeId)
            this.removeFromCart(districtId, line.feeId, OrderItemType.EpaytrakCartFee)
          }
        }
      }
    },
    addToCart(districtId: TDistrictId, feeData: Omit<TEPayTrakFeeCartLine, 'uniqueId'>) {
      const cartStore = useCartStore()
      const uniqueId = createUniqueId(feeData.feeId, feeData.orderItemType)
      let existing = cartStore.getCartLine(districtId, uniqueId)
      if (existing) {
        cartStore.updateCartLine(districtId, uniqueId, feeData)
      } else {
        cartStore.addCartLine(districtId, uniqueId, {
          ...feeData,
          uniqueId
        })
      }
    },

    updateCartLine(
      districtId: TDistrictId,
      feeData: Partial<TEPayTrakFeeCartLine> & {
        feeId: number
        orderItemType: OrderItemType
      }
    ) {
      const cartStore = useCartStore()
      const uniqueId = createUniqueId(feeData.feeId, feeData.orderItemType)
      cartStore.updateCartLine(districtId, uniqueId, feeData)
    },

    removeFromCart(districtId: TDistrictId, feeId: number, orderItemType: OrderItemType) {
      const cartStore = useCartStore()
      const uniqueId = createUniqueId(feeId, orderItemType)
      cartStore.removeCartLine(districtId, uniqueId)
    }
  }
})
